import React, { useState, useEffect } from 'react';
import { FillingIngredient, FillingComponentBatch } from '../../../types/fillingRecord';
import { AvailableComponentBatch } from '../../../types/fillingBatchComponentBatch';
import { LotNumberOption } from '../../../types/componentRecord';
import { fillingApiService } from '../../../services/fillingApiService';
import { fillingBatchComponentBatchApiService } from '../../../services/fillingBatchComponentBatchApiService';
import { ingredientTraceCodeApiService } from '../../../services/ingredientTraceCodeApiService';

interface FillingIngredientsTableProps {
  ingredients: FillingIngredient[];
  componentBatches: FillingComponentBatch[];
  isEditing: boolean;
  onChange: (ingredients: FillingIngredient[]) => void;
  onComponentBatchesChange: (componentBatches: FillingComponentBatch[]) => void;
  fillingId?: string;  // The selected filling/product ID
  timesProduced?: number;
}

// Grouped batches by component name for display
interface GroupedComponentBatches {
  componentName: string;
  selectedBatchIds: string[];
  availableBatches: AvailableComponentBatch[];
}

export const FillingIngredientsTable: React.FC<FillingIngredientsTableProps> = ({
  ingredients,
  componentBatches,
  isEditing,
  onChange,
  onComponentBatchesChange,
  fillingId,
  timesProduced = 1
}) => {
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editingIngredientId, setEditingIngredientId] = useState<string | null>(null);

  // Dropdown data state
  const [availableIngredients, setAvailableIngredients] = useState<string[]>([]);

  // Loading states
  const [ingredientsLoading, setIngredientsLoading] = useState(false);

  // Lot numbers state (per-ingredient mapping)
  const [lotNumbersByIngredient, setLotNumbersByIngredient] = useState<Record<string, LotNumberOption[]>>({});
  const [loadingIngredients, setLoadingIngredients] = useState<Record<string, boolean>>({});

  // New ingredient form state
  const [newIngredientName, setNewIngredientName] = useState('');
  const [newLotNumbers, setNewLotNumbers] = useState('');
  const [newQuantity, setNewQuantity] = useState('');
  const [newUnit, setNewUnit] = useState('');
  const [selectedLotNumbers, setSelectedLotNumbers] = useState<string[]>([]);

  // Edit ingredient form state
  const [editIngredientName, setEditIngredientName] = useState('');
  const [editLotNumbers, setEditLotNumbers] = useState('');
  const [editQuantity, setEditQuantity] = useState('');
  const [editUnit, setEditUnit] = useState('');
  const [editSelectedLotNumbers, setEditSelectedLotNumbers] = useState<string[]>([]);

  // Component batch state
  const [availableComponentBatches, setAvailableComponentBatches] = useState<AvailableComponentBatch[]>([]);
  const [componentBatchesLoading, setComponentBatchesLoading] = useState(false);
  const [groupedBatches, setGroupedBatches] = useState<GroupedComponentBatches[]>([]);

  // Fetch ingredient names when fillingId changes
  useEffect(() => {
    if (fillingId && isEditing) {
      fetchIngredientNames();
    }
  }, [fillingId, isEditing]);

  // Fetch available component batches when fillingId changes
  useEffect(() => {
    if (fillingId && isEditing) {
      fetchAvailableComponentBatches();
    }
  }, [fillingId, isEditing]);

  // Fetch trace codes for all ingredients when editing mode is enabled
  useEffect(() => {
    if (isEditing && ingredients.length > 0) {
      ingredients.forEach(ingredient => {
        if (!lotNumbersByIngredient[ingredient.ingredientName] && !loadingIngredients[ingredient.ingredientName]) {
          fetchTraceCodesForIngredient(ingredient.ingredientName);
        }
      });
    }
  }, [isEditing, ingredients]);

  // Group available batches by component name and merge with selected batches
  useEffect(() => {
    // Group available batches by component name
    const groupedByComponent: Record<string, AvailableComponentBatch[]> = {};
    availableComponentBatches.forEach(batch => {
      if (!groupedByComponent[batch.componentName]) {
        groupedByComponent[batch.componentName] = [];
      }
      groupedByComponent[batch.componentName].push(batch);
    });

    // Build grouped batches with selections
    const groups: GroupedComponentBatches[] = Object.entries(groupedByComponent).map(([componentName, batches]) => {
      const selectedIds = componentBatches
        .filter(cb => cb.componentName === componentName)
        .map(cb => cb.componentBatchId);
      return {
        componentName,
        selectedBatchIds: selectedIds,
        availableBatches: batches
      };
    });

    setGroupedBatches(groups);
  }, [availableComponentBatches, componentBatches]);

  const fetchIngredientNames = async () => {
    if (!fillingId) return;

    setIngredientsLoading(true);
    try {
      const ingredients = await fillingApiService.fetchFillingIngredients(fillingId);
      setAvailableIngredients(ingredients);
    } catch (error) {
      console.error('Failed to fetch ingredient names:', error);
      setAvailableIngredients([]);
    } finally {
      setIngredientsLoading(false);
    }
  };

  // Fetch trace codes for an ingredient from API
  const fetchTraceCodesForIngredient = async (ingredientName: string) => {
    if (!ingredientName || loadingIngredients[ingredientName]) return;

    setLoadingIngredients(prev => ({ ...prev, [ingredientName]: true }));
    try {
      const traceCodes = await ingredientTraceCodeApiService.fetchTraceCodesByIngredientName(ingredientName);
      setLotNumbersByIngredient(prev => ({ ...prev, [ingredientName]: traceCodes }));
    } catch (error) {
      console.error(`Failed to fetch trace codes for ${ingredientName}:`, error);
      setLotNumbersByIngredient(prev => ({ ...prev, [ingredientName]: [] }));
    } finally {
      setLoadingIngredients(prev => ({ ...prev, [ingredientName]: false }));
    }
  };

  const fetchAvailableComponentBatches = async () => {
    if (!fillingId) return;

    setComponentBatchesLoading(true);
    try {
      const batches = await fillingBatchComponentBatchApiService.fetchAvailableComponentBatchesForFilling(fillingId);
      setAvailableComponentBatches(batches);
    } catch (error) {
      console.error('Failed to fetch available component batches:', error);
      setAvailableComponentBatches([]);
    } finally {
      setComponentBatchesLoading(false);
    }
  };

  const handleComponentBatchToggle = (batchId: string, batch: AvailableComponentBatch, isCurrentlySelected: boolean) => {
    if (isCurrentlySelected) {
      // Remove batch
      onComponentBatchesChange(componentBatches.filter(cb => cb.componentBatchId !== batchId));
    } else {
      // Add batch
      const newCb: FillingComponentBatch = {
        id: `temp-cb-${Date.now()}`,
        componentBatchId: batchId,
        batchLotNumber: batch.batchLotNumber,
        componentName: batch.componentName
      };
      onComponentBatchesChange([...componentBatches, newCb]);
    }
  };

  const handleDeleteComponentBatches = (componentName: string) => {
    if (window.confirm(`Remove all batch selections for "${componentName}"?`)) {
      onComponentBatchesChange(componentBatches.filter(cb => cb.componentName !== componentName));
    }
  };

  const handleAddIngredient = () => {
    setIsAddingNew(true);
    setNewIngredientName('');
    setNewLotNumbers('');
    setNewQuantity('');
    setNewUnit('');
    setSelectedLotNumbers([]);
  };

  const handleCancelAdd = () => {
    setIsAddingNew(false);
    setNewIngredientName('');
    setNewLotNumbers('');
    setNewQuantity('');
    setNewUnit('');
    setSelectedLotNumbers([]);
  };

  const handleSaveNew = () => {
    if (!newIngredientName.trim()) {
      alert('Please select an ingredient name');
      return;
    }

    if (selectedLotNumbers.length === 0) {
      alert('Please select at least one lot number');
      return;
    }

    // Validate quantity if provided
    let quantityValue: number | undefined = undefined;
    if (newQuantity.trim() !== '') {
      quantityValue = parseFloat(newQuantity);
      if (isNaN(quantityValue) || quantityValue <= 0) {
        alert('Please enter a valid quantity greater than 0, or leave it empty');
        return;
      }
    }

    const newIngredient: FillingIngredient = {
      id: `${Date.now()}-${Math.random()}`,
      ingredientName: newIngredientName.trim(),
      lotNumbers: selectedLotNumbers,  // Now an array
      quantity: quantityValue,
      unit: newUnit.trim() !== '' ? newUnit : undefined
    };

    onChange([...ingredients, newIngredient]);
    handleCancelAdd();
  };

  const handleEditIngredient = (ingredient: FillingIngredient) => {
    setEditingIngredientId(ingredient.id);
    setEditIngredientName(ingredient.ingredientName);

    // Convert string to array for backward compatibility
    const lotNumbersArray = Array.isArray(ingredient.lotNumbers)
      ? ingredient.lotNumbers
      : ingredient.lotNumbers.split(',').map(s => s.trim()).filter(s => s.length > 0);

    setEditSelectedLotNumbers(lotNumbersArray);
    setEditLotNumbers(lotNumbersArray.join(', '));
    setEditQuantity(ingredient.quantity?.toString() || '');
    setEditUnit(ingredient.unit || '');

    // Fetch lot numbers for this ingredient
    fetchTraceCodesForIngredient(ingredient.ingredientName);
  };

  const handleCancelEdit = () => {
    setEditingIngredientId(null);
    setEditIngredientName('');
    setEditLotNumbers('');
    setEditQuantity('');
    setEditUnit('');
    setEditSelectedLotNumbers([]);
  };

  const handleSaveEdit = () => {
    if (!editIngredientName.trim()) {
      alert('Please select an ingredient name');
      return;
    }

    if (editSelectedLotNumbers.length === 0) {
      alert('Please select at least one lot number');
      return;
    }

    // Validate quantity if provided
    let quantityValue: number | undefined = undefined;
    if (editQuantity.trim() !== '') {
      quantityValue = parseFloat(editQuantity);
      if (isNaN(quantityValue) || quantityValue <= 0) {
        alert('Please enter a valid quantity greater than 0, or leave it empty');
        return;
      }
    }

    const updatedIngredients = ingredients.map(ing =>
      ing.id === editingIngredientId
        ? {
            ...ing,
            ingredientName: editIngredientName.trim(),
            lotNumbers: editSelectedLotNumbers,  // Now an array
            quantity: quantityValue,
            unit: editUnit.trim() !== '' ? editUnit : undefined
          }
        : ing
    );

    onChange(updatedIngredients);
    handleCancelEdit();
  };

  const handleDeleteIngredient = (id: string, ingredientName: string) => {
    if (window.confirm(`Are you sure you want to delete "${ingredientName}"?`)) {
      onChange(ingredients.filter(ing => ing.id !== id));
    }
  };

  const handleLotNumberToggle = (ingredientId: string, lotNumber: string, currentLotNumbers: string[]) => {
    const isSelected = currentLotNumbers.includes(lotNumber);
    const newLotNumbers = isSelected
      ? currentLotNumbers.filter(ln => ln !== lotNumber)
      : [...currentLotNumbers, lotNumber];

    onChange(ingredients.map(ing =>
      ing.id === ingredientId
        ? { ...ing, lotNumbers: newLotNumbers }
        : ing
    ));
  };

  return (
    <div>
      <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', marginBottom: '16px' }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse'
          }}
        >
          <thead>
            <tr
              style={{
                backgroundColor: '#f8f9fa',
                borderBottom: '2px solid #e0e0e0'
              }}
            >
              <th
                style={{
                  padding: '12px',
                  textAlign: 'left',
                  fontWeight: '600',
                  color: '#555',
                  fontSize: '13px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}
              >
                Ingredient Name
              </th>
              <th
                style={{
                  padding: '12px',
                  textAlign: 'left',
                  fontWeight: '600',
                  color: '#555',
                  fontSize: '13px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}
              >
                Lot Numbers
              </th>
              <th
                style={{
                  padding: '12px',
                  textAlign: 'left',
                  fontWeight: '600',
                  color: '#555',
                  fontSize: '13px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  width: '120px'
                }}
              >
                Quantity
              </th>
              <th
                style={{
                  padding: '12px',
                  textAlign: 'left',
                  fontWeight: '600',
                  color: '#555',
                  fontSize: '13px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  width: '100px'
                }}
              >
                Unit
              </th>
              {isEditing && (
                <th
                  style={{
                    padding: '12px',
                    textAlign: 'center',
                    fontWeight: '600',
                    color: '#555',
                    fontSize: '13px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    width: '100px'
                  }}
                >
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {ingredients.map(ingredient => {
              const isRowEditing = editingIngredientId === ingredient.id;
              const availableLots = lotNumbersByIngredient[ingredient.ingredientName] || [];
              const isLoadingLots = loadingIngredients[ingredient.ingredientName];
              return (
                <tr
                  key={ingredient.id}
                  style={{
                    backgroundColor: isRowEditing ? '#f9f9f9' : 'white',
                    borderBottom: '1px solid #e0e0e0'
                  }}
                >
                  <td style={{ padding: '12px' }}>
                    {isRowEditing ? (
                      <select
                        value={editIngredientName}
                        onChange={(e) => {
                          setEditIngredientName(e.target.value);
                          setEditSelectedLotNumbers([]); // Reset lot numbers on ingredient change
                          if (e.target.value) {
                            fetchTraceCodesForIngredient(e.target.value);
                          }
                        }}
                        disabled={ingredientsLoading}
                        style={{
                          width: '100%',
                          padding: '8px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '14px',
                          cursor: ingredientsLoading ? 'wait' : 'pointer'
                        }}
                      >
                        <option value="">
                          {ingredientsLoading ? 'Loading...' : 'Select ingredient...'}
                        </option>
                        {availableIngredients.map(name => (
                          <option key={name} value={name}>
                            {name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span style={{ fontSize: '14px', color: '#333' }}>
                        {ingredient.ingredientName}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '12px' }}>
                    {isEditing ? (
                      isRowEditing ? (
                        // Row being actively edited - use editSelectedLotNumbers
                        (() => {
                          const editAvailableLots = lotNumbersByIngredient[editIngredientName] || [];
                          const isLoading = loadingIngredients[editIngredientName];
                          return editAvailableLots.length === 0 && !isLoading ? (
                            <div style={{
                              padding: '8px',
                              color: '#999',
                              fontSize: '14px',
                              fontStyle: 'italic',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              backgroundColor: '#f9f9f9'
                            }}>
                              {editIngredientName ? 'No trace codes available' : 'Select ingredient first'}
                            </div>
                          ) : (
                            <div style={{
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              maxHeight: '150px',
                              overflowY: 'auto',
                              padding: '8px',
                              backgroundColor: 'white'
                            }}>
                              {isLoading ? (
                                <div style={{ padding: '8px', color: '#999', fontSize: '14px' }}>Loading...</div>
                              ) : (
                                editAvailableLots.map(lot => (
                                  <label
                                    key={lot.lotNumber}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      padding: '4px',
                                      cursor: 'pointer',
                                      fontSize: '14px'
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={editSelectedLotNumbers.includes(lot.lotNumber)}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setEditSelectedLotNumbers([...editSelectedLotNumbers, lot.lotNumber]);
                                        } else {
                                          setEditSelectedLotNumbers(
                                            editSelectedLotNumbers.filter(num => num !== lot.lotNumber)
                                          );
                                        }
                                      }}
                                      style={{ marginRight: '8px' }}
                                    />
                                    <span style={{ fontFamily: 'monospace' }}>{lot.lotNumber}</span>
                                    {lot.receivingDate && (
                                      <span style={{
                                        marginLeft: 'auto',
                                        fontSize: '12px',
                                        color: '#666'
                                      }}>
                                        {new Date(lot.receivingDate).toLocaleDateString()}
                                      </span>
                                    )}
                                  </label>
                                ))
                              )}
                            </div>
                          );
                        })()
                      ) : (
                        // Not row editing but in edit mode - show inline checkboxes
                        isLoadingLots ? (
                          <div style={{ padding: '8px', color: '#999', fontSize: '14px' }}>
                            Loading...
                          </div>
                        ) : availableLots.length > 0 ? (
                          <div
                            style={{
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              maxHeight: '150px',
                              overflowY: 'auto',
                              padding: '8px',
                              backgroundColor: 'white'
                            }}
                          >
                            {availableLots.map(lot => (
                              <label
                                key={lot.lotNumber}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  padding: '4px',
                                  cursor: 'pointer',
                                  fontSize: '14px'
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={ingredient.lotNumbers.includes(lot.lotNumber)}
                                  onChange={() => handleLotNumberToggle(ingredient.id, lot.lotNumber, ingredient.lotNumbers)}
                                  style={{ marginRight: '8px' }}
                                />
                                <span style={{ fontFamily: 'monospace' }}>{lot.lotNumber}</span>
                                {lot.receivingDate && (
                                  <span
                                    style={{
                                      marginLeft: 'auto',
                                      fontSize: '12px',
                                      color: '#666'
                                    }}
                                  >
                                    {new Date(lot.receivingDate).toLocaleDateString()}
                                  </span>
                                )}
                              </label>
                            ))}
                          </div>
                        ) : (
                          <div
                            style={{
                              padding: '8px',
                              color: '#999',
                              fontSize: '14px',
                              fontStyle: 'italic',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              backgroundColor: '#f9f9f9'
                            }}
                          >
                            No lots available
                          </div>
                        )
                      )
                    ) : (
                      <span
                        style={{
                          fontSize: '13px',
                          color: '#333',
                          fontFamily: 'monospace',
                          display: 'block'
                        }}
                      >
                        {Array.isArray(ingredient.lotNumbers)
                          ? ingredient.lotNumbers.join(', ') || '-'
                          : ingredient.lotNumbers || '-'}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '12px' }}>
                    {isRowEditing ? (
                      <input
                        type="number"
                        value={editQuantity}
                        onChange={(e) => setEditQuantity(e.target.value)}
                        placeholder="Optional"
                        step="0.01"
                        style={{
                          width: '100%',
                          padding: '8px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '14px'
                        }}
                      />
                    ) : (
                      <span style={{ fontSize: '14px', color: '#333' }}>
                        {ingredient.quantity !== undefined ? (
                          timesProduced > 1 ? (
                            <>
                              {ingredient.quantity * timesProduced}
                              <span style={{ color: '#888', fontSize: '12px', marginLeft: '4px' }}>
                                ({ingredient.quantity} x {timesProduced})
                              </span>
                            </>
                          ) : (
                            ingredient.quantity
                          )
                        ) : '-'}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '12px' }}>
                    {isRowEditing ? (
                      <select
                        value={editUnit}
                        onChange={(e) => setEditUnit(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '14px'
                        }}
                      >
                        <option value="">Optional...</option>
                        <option value="kg">kg</option>
                        <option value="g">g</option>
                        <option value="lb">lb</option>
                        <option value="oz">oz</option>
                        <option value="L">L</option>
                        <option value="mL">mL</option>
                        <option value="gal">gal</option>
                        <option value="qt">qt</option>
                        <option value="pt">pt</option>
                        <option value="cup">cup</option>
                        <option value="tbsp">tbsp</option>
                        <option value="tsp">tsp</option>
                        <option value="unit">unit</option>
                      </select>
                    ) : (
                      <span style={{ fontSize: '14px', color: '#333' }}>
                        {ingredient.unit || '-'}
                      </span>
                    )}
                  </td>
                  {isEditing && (
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      {editingIngredientId === ingredient.id ? (
                        <>
                          <button
                            onClick={handleSaveEdit}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#4CAF50',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '13px',
                              marginRight: '4px'
                            }}
                          >
                            Save
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#757575',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '13px'
                            }}
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => handleEditIngredient(ingredient)}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#2196F3',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '13px',
                              marginRight: '4px'
                            }}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteIngredient(ingredient.id, ingredient.ingredientName)}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#f44336',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '13px'
                            }}
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
            {isAddingNew && isEditing && (
              <tr style={{ backgroundColor: '#f9f9f9' }}>
                <td style={{ padding: '12px' }}>
                  <select
                    value={newIngredientName}
                    onChange={(e) => {
                      setNewIngredientName(e.target.value);
                      setSelectedLotNumbers([]); // Reset lot numbers on ingredient change
                      if (e.target.value) {
                        fetchTraceCodesForIngredient(e.target.value);
                      }
                    }}
                    disabled={ingredientsLoading}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px',
                      cursor: ingredientsLoading ? 'wait' : 'pointer'
                    }}
                  >
                    <option value="">
                      {ingredientsLoading ? 'Loading...' : 'Select ingredient...'}
                    </option>
                    {availableIngredients.map(name => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                </td>
                <td style={{ padding: '12px' }}>
                  {(() => {
                    const availableLots = lotNumbersByIngredient[newIngredientName] || [];
                    const isLoading = loadingIngredients[newIngredientName];
                    return availableLots.length === 0 && !isLoading ? (
                      <div style={{
                        padding: '8px',
                        color: '#999',
                        fontSize: '14px',
                        fontStyle: 'italic',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        backgroundColor: '#f9f9f9'
                      }}>
                        {newIngredientName ? 'No trace codes available' : 'Select ingredient first'}
                      </div>
                    ) : (
                      <div style={{
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        maxHeight: '150px',
                        overflowY: 'auto',
                        padding: '8px',
                        backgroundColor: 'white'
                      }}>
                        {isLoading ? (
                          <div style={{ padding: '8px', color: '#999', fontSize: '14px' }}>Loading...</div>
                        ) : (
                          availableLots.map(lot => (
                            <label
                              key={lot.lotNumber}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '4px',
                                cursor: 'pointer',
                                fontSize: '14px'
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={selectedLotNumbers.includes(lot.lotNumber)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedLotNumbers([...selectedLotNumbers, lot.lotNumber]);
                                  } else {
                                    setSelectedLotNumbers(
                                      selectedLotNumbers.filter(num => num !== lot.lotNumber)
                                    );
                                  }
                                }}
                                style={{ marginRight: '8px' }}
                              />
                              <span style={{ fontFamily: 'monospace' }}>{lot.lotNumber}</span>
                              {lot.receivingDate && (
                                <span style={{
                                  marginLeft: 'auto',
                                  fontSize: '12px',
                                  color: '#666'
                                }}>
                                  {new Date(lot.receivingDate).toLocaleDateString()}
                                </span>
                              )}
                            </label>
                          ))
                        )}
                      </div>
                    );
                  })()}
                </td>
                <td style={{ padding: '12px' }}>
                  <input
                    type="number"
                    value={newQuantity}
                    onChange={(e) => setNewQuantity(e.target.value)}
                    placeholder="Optional"
                    step="0.01"
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                </td>
                <td style={{ padding: '12px' }}>
                  <select
                    value={newUnit}
                    onChange={(e) => setNewUnit(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">Optional...</option>
                    <option value="kg">kg</option>
                    <option value="g">g</option>
                    <option value="lb">lb</option>
                    <option value="oz">oz</option>
                    <option value="L">L</option>
                    <option value="mL">mL</option>
                    <option value="gal">gal</option>
                    <option value="qt">qt</option>
                    <option value="pt">pt</option>
                    <option value="cup">cup</option>
                    <option value="tbsp">tbsp</option>
                    <option value="tsp">tsp</option>
                    <option value="unit">unit</option>
                  </select>
                </td>
                <td style={{ padding: '12px', textAlign: 'center' }}>
                  <button
                    onClick={handleSaveNew}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#4CAF50',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      marginRight: '4px'
                    }}
                  >
                    Save
                  </button>
                  <button
                    onClick={handleCancelAdd}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#757575',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '13px'
                    }}
                  >
                    Cancel
                  </button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {!isAddingNew && isEditing && (
        <button
          onClick={handleAddIngredient}
          style={{
            padding: '10px 16px',
            backgroundColor: '#4CAF50',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          <span style={{ fontSize: '16px' }}>+</span>
          Add Ingredient
        </button>
      )}

      {/* Component Batches Section */}
      {groupedBatches.length > 0 && (
        <div style={{ marginTop: '24px' }}>
          <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: '#555' }}>
            Component Batches
          </h4>
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse'
              }}
            >
              <thead>
                <tr
                  style={{
                    backgroundColor: '#f8f9fa',
                    borderBottom: '2px solid #e0e0e0'
                  }}
                >
                  <th
                    style={{
                      padding: '12px',
                      textAlign: 'left',
                      fontWeight: '600',
                      color: '#555',
                      fontSize: '13px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}
                  >
                    Component Name
                  </th>
                  <th
                    style={{
                      padding: '12px',
                      textAlign: 'left',
                      fontWeight: '600',
                      color: '#555',
                      fontSize: '13px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}
                  >
                    Batch Numbers
                  </th>
                  {isEditing && (
                    <th
                      style={{
                        padding: '12px',
                        textAlign: 'center',
                        fontWeight: '600',
                        color: '#555',
                        fontSize: '13px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        width: '100px'
                      }}
                    >
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {groupedBatches.map(group => {
                  const selectedBatchLotNumbers = componentBatches
                    .filter(cb => cb.componentName === group.componentName)
                    .map(cb => cb.batchLotNumber);
                  return (
                    <tr
                      key={group.componentName}
                      style={{
                        backgroundColor: 'white',
                        borderBottom: '1px solid #e0e0e0'
                      }}
                    >
                      <td style={{ padding: '12px', verticalAlign: 'top' }}>
                        <span style={{ fontSize: '14px', color: '#333', fontWeight: '500' }}>
                          {group.componentName}
                        </span>
                      </td>
                      <td style={{ padding: '12px' }}>
                        {isEditing ? (
                          <div
                            style={{
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              maxHeight: '150px',
                              overflowY: 'auto',
                              padding: '8px',
                              backgroundColor: 'white'
                            }}
                          >
                            {group.availableBatches.map(batch => {
                              const isSelected = group.selectedBatchIds.includes(batch.id);
                              return (
                                <label
                                  key={batch.id}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: '4px',
                                    cursor: 'pointer',
                                    fontSize: '14px'
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => handleComponentBatchToggle(batch.id, batch, isSelected)}
                                    style={{ marginRight: '8px' }}
                                  />
                                  <span style={{ fontFamily: 'monospace' }}>{batch.batchLotNumber}</span>
                                  <span
                                    style={{
                                      marginLeft: 'auto',
                                      fontSize: '12px',
                                      color: '#666'
                                    }}
                                  >
                                    {new Date(batch.dateOfProduction).toLocaleDateString()}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        ) : (
                          <span
                            style={{
                              fontSize: '13px',
                              color: '#333',
                              fontFamily: 'monospace',
                              display: 'block'
                            }}
                          >
                            {selectedBatchLotNumbers.length > 0
                              ? selectedBatchLotNumbers.join(', ')
                              : '-'}
                          </span>
                        )}
                      </td>
                      {isEditing && (
                        <td style={{ padding: '12px', textAlign: 'center', verticalAlign: 'top' }}>
                          <button
                            onClick={() => handleDeleteComponentBatches(group.componentName)}
                            disabled={group.selectedBatchIds.length === 0}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: group.selectedBatchIds.length > 0 ? '#f44336' : '#ccc',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: group.selectedBatchIds.length > 0 ? 'pointer' : 'not-allowed',
                              fontSize: '13px'
                            }}
                          >
                            Delete
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {ingredients.length === 0 && !isAddingNew && (
        <div
          style={{
            padding: '20px',
            textAlign: 'center',
            color: '#999',
            fontSize: '14px',
            fontStyle: 'italic'
          }}
        >
          {isEditing ? 'No ingredients added yet. Click "Add Ingredient" to get started.' : 'No ingredients added yet.'}
        </div>
      )}
    </div>
  );
};
